import type { QueryClient } from "@tanstack/query-core";
import { queryKeys, withSessionQueryScope } from "~/lib/query-keys";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import { normalizeAssetDomainAssetIds } from "./models";
import { applyAssetOverlayBatchToCollections } from "./asset-domain-hydration";
import { chunkArray } from "./collection-query-support";
import { getAssetDomainCollections } from "./collection-registry";
import { getAssetDomainDependencies } from "./dependencies";

export const ASSET_OVERLAY_BATCH_LIMIT = 50;
const ASSET_OVERLAY_CHUNK_STALE_TIME_MS = 30_000;

export function getAssetDomainOverlayQueryOptions(input: {
  assetIds: readonly string[];
  platformApi?: PlatformApiTrpcClient;
  queryClient?: QueryClient;
  sessionScopeToken?: string;
}) {
  const normalizedAssetIds = normalizeAssetDomainAssetIds(input.assetIds);
  const dependencies = getAssetDomainDependencies({
    platformApi: input.platformApi,
    queryClient: input.queryClient,
    sessionScopeToken: input.sessionScopeToken,
  });
  const sessionScope = {
    scopeToken: dependencies.sessionScopeToken,
  };
  const assetIdsKey = normalizedAssetIds.join("|");

  return {
    enabled: normalizedAssetIds.length > 0,
    queryKey: withSessionQueryScope(
      queryKeys.assetDomain.overlayHydrationByKey(assetIdsKey),
      sessionScope,
    ),
    queryFn: async () =>
      await ensureAssetDomainOverlays(normalizedAssetIds, {
        platformApi: dependencies.platformApi,
        queryClient: dependencies.queryClient,
        sessionScopeToken: dependencies.sessionScopeToken,
      }),
    // The outer query coordinates each mounted consumer. Keep it stale so a
    // remount can replay a still-fresh chunk response into TanStack DB.
    staleTime: 0,
  } as const;
}

function getAssetOverlayChunkQueryOptions(input: {
  assetIds: readonly string[];
  platformApi?: PlatformApiTrpcClient;
  queryClient?: QueryClient;
  sessionScopeToken?: string;
}) {
  const normalizedAssetIds = normalizeAssetDomainAssetIds(input.assetIds);
  const dependencies = getAssetDomainDependencies({
    platformApi: input.platformApi,
    queryClient: input.queryClient,
    sessionScopeToken: input.sessionScopeToken,
  });
  const sessionScope = {
    scopeToken: dependencies.sessionScopeToken,
  };
  const assetIdsKey = normalizedAssetIds.join("|");

  return {
    enabled: normalizedAssetIds.length > 0,
    queryKey: withSessionQueryScope(
      queryKeys.assetDomain.overlayChunkByKey(assetIdsKey),
      sessionScope,
    ),
    queryFn: async () => {
      return await dependencies.platformApi.assets.getAssetOverlaysBatch.query({
        assetIds: normalizedAssetIds,
      });
    },
    staleTime: ASSET_OVERLAY_CHUNK_STALE_TIME_MS,
  } as const;
}

export async function ensureAssetDomainOverlays(
  assetIds: readonly string[],
  options?: {
    platformApi?: PlatformApiTrpcClient;
    queryClient?: QueryClient;
    sessionScopeToken?: string;
  },
): Promise<string[]> {
  const normalizedAssetIds = normalizeAssetDomainAssetIds(assetIds);
  if (normalizedAssetIds.length === 0) {
    return [];
  }

  const dependencies = getAssetDomainDependencies({
    platformApi: options?.platformApi,
    queryClient: options?.queryClient,
    sessionScopeToken: options?.sessionScopeToken,
  });
  const collections = getAssetDomainCollections({
    sessionScopeToken: dependencies.sessionScopeToken,
  });
  const chunks = chunkArray(normalizedAssetIds, ASSET_OVERLAY_BATCH_LIMIT);

  await Promise.all(
    chunks.map(async (chunk) => {
      const queryOptions = getAssetOverlayChunkQueryOptions({
        assetIds: chunk,
        platformApi: dependencies.platformApi,
        queryClient: dependencies.queryClient,
        sessionScopeToken: dependencies.sessionScopeToken,
      });
      const { enabled: _enabled, ...fetchQueryOptions } = queryOptions;
      const response =
        await dependencies.queryClient.fetchQuery(fetchQueryOptions);
      await applyAssetOverlayBatchToCollections({
        collections,
        normalizedAssetIds: chunk,
        response,
      });
    }),
  );

  return normalizedAssetIds;
}

/** @deprecated Use ensureAssetDomainOverlays. */
export const ensureAssetOverlays = ensureAssetDomainOverlays;
